import React, { useEffect, useState } from "react";
import { Card, Col, Form, Row, Button, Table, Dropdown } from "react-bootstrap";
import schoolApi from "../../api/schoolApi";
import ScrollLazyLoader from "../common/ScrollLazyLoader";
import { Link } from "react-router-dom";
import jsPDF from "jspdf";
import "jspdf-autotable";
import * as XLSX from "xlsx";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import moment from "moment";
import PdfHeader from "../layout/common/PdfHeader";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Helmet } from "react-helmet";
import jwt_decode from "jwt-decode";
import {
  DatatableWrapper,
  Filter,
  Pagination,
  PaginationOptions,
  TableBody,
  TableHeader,
} from "react-bs-datatable";
import InfoPill from "../InfoPill";

const FeeCollectionReport = (props) => {
  const [classOptions, setClassOptions] = useState([]);
  const [sessionOptions, setSessionOptions] = useState([]);
  const [reportData, setReportData] = useState({
    classid: "",
    session_id: "",
    start_date: null,
    end_date: null,
  });
  const [feeData, setFeeData] = useState([]);
  const [userInfo, setUserInfo] = useState({});

  useEffect(() => {
    fetchClassRecords();
    fetchAllSessions();
  }, []);

  useEffect(() => {
    try {
      if (sessionStorage.getItem("token")) {
        let user = jwt_decode(sessionStorage.getItem("token"));
        setUserInfo(user);
      }
    } catch (error) {
      console.error("Error decoding token:", error);
    }
  }, []);

  const fetchClassRecords = async () => {
    try {
      const result = await schoolApi.getActiveClassRecords();
      if (result.success) {
        const options = result.result.map((item) => ({
          value: item.id,
          label: item.classname,
        }));
        setClassOptions(options);
      }
    } catch (error) {
      console.error("Error fetching class records:", error);
    }
  };

  const fetchAllSessions = async () => {
    try {
      const sessions = await schoolApi.fetchSessions();

      if (sessions.success) {
        const options = sessions?.result?.map((sess) => ({
          value: sess.id,
          label: sess.year,
        }));
        setSessionOptions(options);

        const currentSession = sessions?.result?.find((sess) => {
          const currentDate = new Date();
          const startDate = new Date(sess.startdate);
          const endDate = new Date(sess.enddate);
          return currentDate >= startDate && currentDate <= endDate;
        });

        if (currentSession) {
          setReportData((prevState) => ({
            ...prevState,
            session_id: currentSession.id,
          }));
        }
      } else {
        setSessionOptions([]);
        setReportData({
          classid: "",
          session_id: "",
          start_date: null,
          end_date: null,
        });
      }
    } catch (error) {
      console.error("Error fetching sessions:", error);
    }
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setReportData((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const handleDateChange = (date, fieldName) => {
    setReportData((prevState) => ({
      ...prevState,
      [fieldName]: date,
    }));
  };

  const handleSearch = async () => {
    try {
      if (!reportData?.session_id) {
        return toast.error("Please select required fields");
      }

      const payload = {
        ...reportData,
        start_date: reportData.start_date
          ? moment(reportData.start_date).format("YYYY-MM-DD")
          : "",
        end_date: reportData.end_date
          ? moment(reportData.end_date).format("YYYY-MM-DD")
          : "",
      };

      const result = await schoolApi.getAllStudentByClassidFeeData(payload);
      if (result.success) {
        setFeeData(result.result || []);
      } else {
        setFeeData([]);
      }
    } catch (error) {
      setFeeData([]);
      toast.error(error?.response?.data?.message || error.message);
    }
  };

  const currentDate = new Date().toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });

  const totalGrossFees = Array.isArray(feeData)
    ? feeData.reduce((acc, item) => acc + (parseFloat(item?.gross_total_fee) || 0), 0)
    : 0;
  const totalDiscount = Array.isArray(feeData)
    ? feeData.reduce((acc, item) => acc + (parseFloat(item?.total_discount) || 0), 0)
    : 0;
  const totalNetFee = Array.isArray(feeData)
    ? feeData.reduce((acc, item) => acc + (parseFloat(item?.total_fee) || 0), 0)
    : 0;
  const totalPaid = Array.isArray(feeData)
    ? feeData.reduce((acc, item) => acc + (parseFloat(item?.total_deposited) || 0), 0)
    : 0;
  const totalPending = Array.isArray(feeData)
    ? feeData.reduce((acc, item) => acc + (parseFloat(item?.pending_amount_calculated) || 0), 0)
    : 0;
  const otherFee = Array.isArray(feeData)
    ? feeData.reduce((acc, item) => acc + (parseFloat(item?.late_fee || item?.others) || 0), 0)
    : 0;

  const formatNumberWithCommas = (number) => {
    return number !== undefined && number !== null
      ? Number(number).toLocaleString("en-IN")
      : "0";
  };

  const handleDownload = () => {
    const doc = new jsPDF({
      orientation: "landscape",
      unit: "mm",
      format: "a4",
    });

    const renderPdfHeader = (doc) => {
      const logoUrl = `/${userInfo?.tenantcode}-sidebar-logo.png`;
      try {
        doc.addImage(logoUrl, "PNG", 14, 10, 27, 27);
      } catch (e) {
        // ignore image error if missing
      }
      doc.setFontSize(18);
      doc.text(userInfo?.companyname || "School Name", 145, 20, {
        align: "center",
      });
      doc.setFontSize(10);
      const address = `${userInfo?.companystreet || ""}, ${userInfo?.companystate || ""}-${userInfo?.companypincode || ""}, ${userInfo?.companycountry || ""}`;
      const addressLines = doc.splitTextToSize(address, 180);
      doc.text(addressLines, 145, 26, { align: "center" });
      doc.setFontSize(12);
      doc.text(`Date: ${currentDate}`, 280, 20, { align: "right" });

      doc.line(14, 40, 283, 40);
      doc.text("Fee Collection Report", 125, 47);
    };

    renderPdfHeader(doc);

    const tableData = feeData.map((row, index) => [
      index + 1,
      row?.student_name || "",
      row?.parent_name || "",
      row?.parent_phone || "",
      row?.classname + "" + (row.section_name ? `(${row.section_name})` : ""),
      formatNumberWithCommas(row?.gross_total_fee || row?.total_fee),
      formatNumberWithCommas(row?.total_discount || 0),
      formatNumberWithCommas(row?.total_fee || 0),
      formatNumberWithCommas(row?.late_fee || row?.others || 0),
      formatNumberWithCommas(row?.total_deposited || 0),
      formatNumberWithCommas(row?.pending_amount_calculated || 0),
    ]);

    tableData.push([
      "",
      "",
      "",
      "",
      "Total",
      totalGrossFees.toFixed(2),
      totalDiscount.toFixed(2),
      totalNetFee.toFixed(2),
      otherFee.toFixed(2),
      totalPaid.toFixed(2),
      totalPending.toFixed(2),
    ]);

    doc.autoTable({
      head: [
        [
          "Sr. No.",
          "Student Name",
          "Parent Name",
          "Parent Mobile",
          "Class",
          "Total Fees",
          "Discount",
          "Net Fee",
          "Late Fee",
          "Total Paid",
          "Due Amount",
        ],
      ],
      body: tableData,
      didDrawCell: () => {
        doc.setLineDash([1, 1], 0);
      },
      styles: {
        borderTop: "2px solid #000",
        margin: "10px 0",
        border: 1,
        lineWidth: 0.1,
        lineColor: [0, 0, 0],
        fontSize: 9,
      },
      headStyles: {
        fillColor: "rgb(41,56,70)",
        textColor: [255, 255, 255],
        halign: "center",
        fontStyle: "bold",
      },
      columnStyles: {
        0: { halign: "center" },
        5: { halign: "right" },
        6: { halign: "right" },
        7: { halign: "right" },
        8: { halign: "right" },
        9: { halign: "right" },
        10: { halign: "right" },
      },
      margin: { top: 50 },
    });

    doc.save("Fee_Collection_Report.pdf");
  };

  const downloadExcel = () => {
    const wsData = [
      [
        "Sr. No.",
        "Student Name",
        "Parent Name",
        "Parent Mobile",
        "Class",
        "Total Fees (Gross)",
        "Discount",
        "Net Fee",
        "Late Fee",
        "Total Paid",
        "Due Amount",
      ],
      ...feeData.map((row, index) => [
        index + 1,
        row?.student_name || "",
        row?.parent_name || "",
        row?.parent_phone || "",
        `${row.classname || ""} ${row.section_name ? `(${row.section_name})` : ""}`,
        parseFloat(row?.gross_total_fee || row?.total_fee || 0),
        parseFloat(row?.total_discount || 0),
        parseFloat(row?.total_fee || 0),
        parseFloat(row?.late_fee || row?.others || 0),
        parseFloat(row?.total_deposited || 0),
        parseFloat(row?.pending_amount_calculated || 0),
      ]),
      [
        "",
        "",
        "",
        "",
        "Total",
        totalGrossFees,
        totalDiscount,
        totalNetFee,
        otherFee,
        totalPaid,
        totalPending,
      ],
    ];

    const ws = XLSX.utils.aoa_to_sheet(wsData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Fee Collection");
    XLSX.writeFile(wb, `Fee_Collection_Report_${moment().format("DD-MM-YYYY")}.xlsx`);
  };

  const labels = {
    beforeSelect: " ",
  };

  const headers = [
    {
      title: "Sr. No.",
      prop: "serialNumber",
      width: "50px",
      isSortable: true,
      cell: (row) => row.serialNumber,
      style: { textAlign: "left" },
    },
    {
      title: "Student Name",
      prop: "student_name",
      isFilterable: true,
      isSortable: true,
    },
    {
      title: "Parent Name",
      prop: "parent_name",
      isFilterable: true,
      isSortable: true,
    },
    {
      title: "Parent Mobile",
      prop: "parent_phone",
      isFilterable: true,
      isSortable: true,
      cell: (row) => row?.parent_phone || "N/A",
    },
    {
      title: "Class Name",
      prop: "classname",
      isFilterable: true,
      isSortable: true,
      cell: (row) => `${row.classname || ""} ${row.section_name ? `(${row.section_name})` : ""}`,
    },
    {
      title: "Total Fees",
      prop: "gross_total_fee",
      isSortable: true,
      cell: (row) => (
        <div style={{ textAlign: "right" }}>
          {formatNumberWithCommas(row.gross_total_fee || row.total_fee)}
        </div>
      ),
    },
    {
      title: "Discount",
      prop: "total_discount",
      isSortable: true,
      cell: (row) => (
        <div
          style={{
            textAlign: "right",
            color: row.total_discount > 0 ? "#198754" : "inherit",
          }}
        >
          {formatNumberWithCommas(row.total_discount)}
        </div>
      ),
    },
    {
      title: "Net Fee",
      prop: "total_fee",
      isSortable: true,
      cell: (row) => (
        <div style={{ textAlign: "right", fontWeight: "500" }}>
          {formatNumberWithCommas(row.total_fee)}
        </div>
      ),
    },
    {
      title: "Late Fee",
      prop: "late_fee",
      isSortable: true,
      cell: (row) => (
        <div
          style={{
            textAlign: "right",
            color: (row.late_fee || row.others) > 0 ? "#dc3545" : "inherit",
          }}
        >
          {formatNumberWithCommas(row.late_fee || row.others || 0)}
        </div>
      ),
    },
    {
      title: "Total Paid",
      prop: "total_deposited",
      isSortable: true,
      cell: (row) => (
        <div style={{ textAlign: "right", color: "#0d6efd" }}>
          {formatNumberWithCommas(row.total_deposited)}
        </div>
      ),
    },
    {
      title: "Due Amount",
      prop: "pending_amount_calculated",
      isSortable: true,
      cell: (row) => (
        <div
          style={{
            textAlign: "right",
            fontWeight: "bold",
            color: row.pending_amount_calculated > 0 ? "#dc3545" : "#198754",
          }}
        >
          {formatNumberWithCommas(row.pending_amount_calculated)}
        </div>
      ),
    },
  ];

  const body = feeData
    ? feeData.map((student, index) => ({
        ...student,
        serialNumber: index + 1,
      }))
    : [];

  return (
    <>
      <Helmet>
        <title>{props?.tabName || "Fee Collection Report"}</title>
      </Helmet>
      <Row className="g-0">
        <Col lg={3} className="mx-3">
          <Link className="nav-link mx-2" to="/">
            Home <i className="fa-solid fa-chevron-right"></i> Fee Collection Report
          </Link>
        </Col>
      </Row>
      <Row className="mt-5 m-0 px-4">
        <Card className="px-0 py-3" style={{ marginTop: "-17px" }}>
          <Row className="m-0">
            <Col lg={12} className="px-3">
              <h5 className="fw-semibold">Filters</h5>
            </Col>
            <hr />
          </Row>
          <Row className="flex-row mt-3 mb-2 px-4">
            <Col lg={3}>
              <Form.Group className="mx-2">
                <Form.Label className="form-view-label">Session</Form.Label>
                <Form.Select
                  name="session_id"
                  onChange={handleChange}
                  value={reportData.session_id}
                  required
                >
                  <option value="">-- Select Session --</option>
                  {sessionOptions.map((res) => (
                    <option key={res.value} value={res.value}>
                      {res.label}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>

            <Col lg={3}>
              <Form.Group className="mx-2">
                <Form.Label className="form-view-label">Class Name</Form.Label>
                <Form.Select
                  name="classid"
                  onChange={handleChange}
                  value={reportData.classid}
                >
                  <option key="default" value="">
                    All
                  </option>
                  {classOptions.map((res) => (
                    <option key={res.value} value={res.value}>
                      {res.label}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>

            <Col lg={3}>
              <Form.Group className="mx-2">
                <Form.Label className="form-view-label">From Date</Form.Label>
                <DatePicker
                  selected={reportData.start_date}
                  onChange={(date) => handleDateChange(date, "start_date")}
                  dateFormat="dd-MM-yyyy"
                  className="form-control"
                  placeholderText="Select start date"
                  showMonthDropdown
                  showYearDropdown
                  isClearable
                  highlightDates={[new Date()]}
                />
              </Form.Group>
            </Col>

            <Col lg={3}>
              <Form.Group className="mx-2">
                <Form.Label className="form-view-label">To Date</Form.Label>
                <DatePicker
                  selected={reportData.end_date}
                  onChange={(date) => handleDateChange(date, "end_date")}
                  dateFormat="dd-MM-yyyy"
                  className="form-control"
                  placeholderText="Select end date"
                  showMonthDropdown
                  showYearDropdown
                  isClearable
                  highlightDates={[new Date()]}
                />
              </Form.Group>
            </Col>
          </Row>

          <Row className="mt-2 mb-2 px-4">
            <Col lg={12} className="d-flex justify-content-end">
              <div className="button-wrapper">
                <Button onClick={handleSearch} variant="primary" className="me-2">
                  Search
                </Button>
                {feeData.length > 0 && (
                  <Dropdown className="d-inline-block">
                    <Dropdown.Toggle variant="primary" id="download-dropdown">
                      Download
                    </Dropdown.Toggle>

                    <Dropdown.Menu>
                      <Dropdown.Item onClick={handleDownload}>
                        <i className="fa-solid fa-file-pdf mx-2 text-danger"></i>
                        PDF
                      </Dropdown.Item>

                      <Dropdown.Item onClick={downloadExcel}>
                        <i className="fa-solid fa-file-excel mx-2 text-success"></i>
                        Excel
                      </Dropdown.Item>
                    </Dropdown.Menu>
                  </Dropdown>
                )}
              </div>
            </Col>
          </Row>
        </Card>
      </Row>

      {feeData.length > 0 && (
        <Row className="mt-5 m-0 px-4">
          <Card className="px-0 py-3">
            <Row className="flex-row mt-3 mb-2 px-4">
              <Col lg={12} className="p-0">
                <div id="reportContent">
                  <PdfHeader />
                  <div
                    style={{ borderTop: "2px solid #000", margin: "10px 0" }}
                  ></div>
                  <Row className="mt-4">
                    <Col lg={12} className="px-3">
                      <h5
                        style={{ textAlign: "center" }}
                        className="fw-semibold"
                      >
                        Fee Collection Report
                      </h5>
                      <Col lg={12} className="mt-1 px-1">
                        <DatatableWrapper
                          headers={headers}
                          body={body}
                          paginationOptionsProps={{
                            initialState: {
                              rowsPerPage: 50,
                              options: [50, 100, 200, 500, 1000],
                            },
                          }}
                        >
                          <ScrollLazyLoader maxRows={1000} step={50} />
                          {/* Summary KPI Badges */}
                          <div className="d-flex flex-wrap justify-content-center align-items-center gap-2 mb-3 py-2 px-3 bg-light rounded border">
                            <InfoPill
                              className="m-0"
                              left="Total Students"
                              right={feeData?.length || 0}
                            />
                            <InfoPill
                              className="m-0"
                              left="Gross Fees"
                              right={`₹${formatNumberWithCommas(totalGrossFees)}`}
                            />
                            <InfoPill
                              className="m-0"
                              left="Discount"
                              right={`₹${formatNumberWithCommas(totalDiscount)}`}
                            />
                            <InfoPill
                              className="m-0"
                              left="Collected"
                              right={`₹${formatNumberWithCommas(totalPaid)}`}
                            />
                            <InfoPill
                              className="m-0"
                              left="Pending Due"
                              right={`₹${formatNumberWithCommas(totalPending)}`}
                            />
                          </div>

                          {/* Table Controls */}
                          <Row className="mb-3 align-items-center justify-content-between">
                            <Col xs={12} sm={6} className="d-flex align-items-center">
                              <PaginationOptions labels={labels} />
                            </Col>
                            <Col xs={12} sm={6} className="d-flex justify-content-end align-items-center">
                              <Filter />
                            </Col>
                          </Row>
                          <Table
                            striped
                            className="transaction-report-table"
                            responsive
                            id="inventory-table-details"
                          >
                            <TableHeader />

                            {feeData?.length > 0 ? (
                              <>
                                <TableBody />

                                <tfoot>
                                  <tr className="fw-bold" style={{ backgroundColor: "#f8f9fa" }}>
                                    <td
                                      style={{ textAlign: "right" }}
                                      colSpan="5"
                                      className="text-right"
                                    >
                                      Grand Totals:
                                    </td>
                                    <td style={{ textAlign: "right" }}>
                                      {formatNumberWithCommas(totalGrossFees)}
                                    </td>
                                    <td style={{ textAlign: "right", color: totalDiscount > 0 ? "#198754" : "inherit" }}>
                                      {formatNumberWithCommas(totalDiscount)}
                                    </td>
                                    <td style={{ textAlign: "right" }}>
                                      {formatNumberWithCommas(totalNetFee)}
                                    </td>
                                    <td style={{ textAlign: "right", color: otherFee > 0 ? "#dc3545" : "inherit" }}>
                                      {formatNumberWithCommas(otherFee)}
                                    </td>
                                    <td style={{ textAlign: "right", color: "#0d6efd" }}>
                                      {formatNumberWithCommas(totalPaid)}
                                    </td>
                                    <td style={{ textAlign: "right", color: totalPending > 0 ? "#dc3545" : "#198754" }}>
                                      {formatNumberWithCommas(totalPending)}
                                    </td>
                                  </tr>
                                </tfoot>
                              </>
                            ) : (
                              <TableBody>
                                <tr>
                                  <td colSpan={11} className="text-center">
                                    No records found
                                  </td>
                                </tr>
                              </TableBody>
                            )}
                          </Table>
                          <Pagination />
                        </DatatableWrapper>
                      </Col>
                    </Col>
                  </Row>
                </div>
              </Col>
            </Row>
          </Card>
        </Row>
      )}
      <ToastContainer
        position="top-right"
        autoClose={2000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
    </>
  );
};

export default FeeCollectionReport;